import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ValidateRequiredOptions, ValidationErrorInfo } from '@lib/interfaces';
import { AirportComponent } from '@lib/components';
import { ValidateDateRangeOptions } from '@common/interfaces';

@Injectable({
  providedIn: 'root',
})
export class BoardingCertificateSearchValidationService {
  /**
   * 個別入力チェック 名+姓の最大文字数を判定する
   * @param control 航空券番号入力フォーム
   * @returns 55文字以下:null / それ以外:{ isNameOverDigits: true }
   */
  nameDigitsValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const firstName = control.value['firstName'] || '';
    const lastName = control.value['lastName'] || '';

    const sum = firstName.length + lastName.length;

    return sum > 55 ? { isNameOverDigits: true } : null;
  };

  /**
   * 個別入力チェック 出発日/購入日(年/月)
   * @param option エラーメッセージオプション
   * @returns 範囲内: null / 範囲外:'validate-ym-range'
   */
  ymRangeValidator(option: ValidateDateRangeOptions): ValidatorFn {
    const validationError = (errorMsgId?: string) => {
      return {
        'validate-ym-range': {
          errorMsgId: errorMsgId || option.errorMsgId || 'E1282',
          params: option.params,
        },
      };
    };
    const min = option.min;
    const max = option.max;

    return (control: AbstractControl): { [key: string]: ValidationErrorInfo } | null => {
      const value: string = control.value ?? '';

      if (value.length > 0) {
        if (value.length < 6) {
          return validationError('E0002');
        }

        const yearMonth = +value;
        if (yearMonth < min || yearMonth > max) {
          return validationError();
        }
      }

      return null;
    };
  }

  /**
   * 個別入力チェック 出発日/購入日(年/月/日)
   * @param option エラーメッセージオプション
   * @returns 範囲内: null / 範囲外:'validate-ymd-range'
   */
  ymdRangeValidator(option: ValidateDateRangeOptions): ValidatorFn {
    const validationError = {
      'validate-ymd-range': {
        errorMsgId: option.errorMsgId || 'E1282',
        params: option.params,
      },
    };
    const min = option.min;
    const max = option.max;

    return (control: AbstractControl): { [key: string]: ValidationErrorInfo } | null => {
      const yearMonth: string = control.value['searchYearMonth'] ?? '';
      const day: string = control.value['searchDay'] ?? '';

      if (yearMonth.length === 6 && day.length > 0) {
        const date = +`${yearMonth}${day}`;

        if (date < min || date > max) {
          return validationError;
        }
      }

      return null;
    };
  }

  /**
   * 空港選択部品が入力されているかを判定する
   * @param getLocationForm 空港選択部品を取得するファンクション
   * @param option 必須チェック用オプション情報
   * @returns validate-location-required: 入力していない / null: 入力している
   */
  locationRequiredValidator(
    getLocationForm: () => AirportComponent | undefined,
    option: ValidateRequiredOptions
  ): ValidatorFn {
    const validationError = {
      'validate-location-required': {
        errorMsgId: 'E0002',
        params: option.params,
      },
    };

    return (control: AbstractControl): { [key: string]: ValidationErrorInfo } | null => {
      const locationForm = getLocationForm();

      if (locationForm) {
        if (!locationForm.selectedAirport && !control.value) {
          return validationError;
        }
      }

      return null;
    };
  }

  /**
   * 空港選択部品が空港を選択しているかを判定する
   * @param getLocationForm 空港選択部品を取得するファンクション
   * @returns validate-location-selected: 選択していない / null: 選択している
   */
  locationSelectedValidator(getLocationForm: () => AirportComponent | undefined): ValidatorFn {
    const validationError = {
      'validate-location-selected': {
        errorMsgId: 'E1870',
      },
    };

    return (control: AbstractControl): { [key: string]: ValidationErrorInfo } | null => {
      const locationForm = getLocationForm();

      if (locationForm) {
        const { selectedAirport, airportList } = locationForm;

        if (
          (!selectedAirport || control.value !== selectedAirport.name) &&
          !airportList.some((airport) => airport.airport_code === control.value)
        ) {
          return validationError;
        }
      }

      return null;
    };
  }
}
